import { DiagnosticFormData } from '../types/diagnostic';
import securityWeights from '../config/securityWeights.json';

interface SecurityDetail {
  label: string;
  score: number;
  maxScore: number;
  percentage: number;
}

interface SecurityScore {
  score: number;
  details: SecurityDetail[];
  categoryScores: Record<string, number>;
}

interface Recommendation {
  title: string;
  priority: 'high' | 'medium' | 'low';
  items: string[];
}

interface VulnerableArea {
  label: string;
  risk: 'high' | 'medium' | 'low';
  description: string;
}

/**
 * Applique les rendements décroissants à un score de catégorie
 */
function applyCategoryDiminishing(rawScore: number, category: string): number {
  const limits = securityWeights.categoryLimits[category as keyof typeof securityWeights.categoryLimits];
  if (!limits) return rawScore;

  const { maxScore, softCap, diminishingRate } = limits;

  if (rawScore <= softCap) {
    return Math.min(rawScore, maxScore);
  }

  const basePoints = softCap;
  const excessPoints = rawScore - softCap;
  const diminishedPoints = excessPoints * diminishingRate;

  return Math.min(basePoints + diminishedPoints, maxScore);
}

/**
 * Détermine le niveau de risque local
 */
function getLocalRiskLevel(formData: DiagnosticFormData): 'high' | 'medium' | 'low' {
  if (!formData.crimeData) return 'medium';

  const rate = formData.crimeData.tauxPour1000Logements || 0;
  const thresholds = securityWeights.localRiskMultipliers.thresholds;

  if (rate >= thresholds.high) return 'high';
  if (rate >= thresholds.medium) return 'medium';
  return 'low';
}

/**
 * Calcule le score final avec pondération contextuelle
 */
function calculateWeightedScore(
  categoryScores: Record<string, number>,
  formData: DiagnosticFormData
): number {
  const riskLevel = getLocalRiskLevel(formData);
  const weights = securityWeights.contextualWeights[`${riskLevel}Risk` as keyof typeof securityWeights.contextualWeights];

  const weightedProtection = categoryScores.protection * weights.protection;
  const weightedSurveillance = categoryScores.surveillance * weights.surveillance;
  const weightedDeterrence = categoryScores.deterrence * weights.deterrence;

  const totalWeight = weights.protection + weights.surveillance + weights.deterrence;
  let finalScore = (weightedProtection + weightedSurveillance + weightedDeterrence) / totalWeight;

  const synergyThreshold = securityWeights.synergyBonus.threshold;
  const allAboveThreshold = 
    categoryScores.protection >= synergyThreshold &&
    categoryScores.surveillance >= synergyThreshold &&
    categoryScores.deterrence >= synergyThreshold;

  if (allAboveThreshold) {
    finalScore *= securityWeights.synergyBonus.multiplier;
  }

  return Math.round(finalScore);
}

export function calculateSecurityScore(formData: DiagnosticFormData): SecurityScore {
  // Normalisation défensive des champs susceptibles d'être undefined/null
  // (diagnostics anciens, données incomplètes, formats variés)
  const housingType = formData.housingType ?? 'house';
  const floors = formData.floors ?? 0;
  const equipment: string[] = Array.isArray(formData.equipment) ? formData.equipment : [];
  const streetLighting = formData.streetLighting ?? '';
  const streetVisibility = formData.streetVisibility ?? '';
  const neighborhoodType = formData.neighborhoodType ?? '';
  const hasBasement = formData.hasBasement ?? false;
  const hasGarage = formData.hasGarage ?? false;
  const hasAccessibleGarden = formData.hasAccessibleGarden ?? false;
  const hasAccessibleBalcony = formData.hasAccessibleBalcony ?? false;

  // 1. Score de base
  let baseScore = securityWeights.baseScores[housingType] || 0;

  // 2. Impact étages
  // Math.max (et non Math.min) car perFloor est négatif : on plafonne la pénalité à maxPenalty (-10)
  // ex. 2 étages → max(-4, -10) = -4  |  6 étages → max(-12, -10) = -10
  const floorPenalty = Math.max(
    floors * securityWeights.floorImpact.perFloor,
    securityWeights.floorImpact.maxPenalty
  );
  baseScore += floorPenalty;

  if (housingType === 'apartment' && floors >= securityWeights.floorImpact.apartmentBonus.threshold) {
    baseScore += securityWeights.floorImpact.apartmentBonus.bonus;
  }

  // 3. Modificateurs dépendances
  if (hasBasement) {
    baseScore += securityWeights.locationModifiers.hasBasement;
  }

  // Pénalité garage : SEULEMENT si le garage est connecté au logement
  // Pour les appartements, on vérifie garageConnectedToHome
  // Pour les maisons, on suppose toujours un garage connecté
  if (hasGarage) {
    const isGarageVulnerability =
      housingType === 'house' ||
      formData.garageConnectedToHome === true;

    if (isGarageVulnerability) {
      baseScore += securityWeights.locationModifiers.hasGarage;
    }
  }

  if (hasAccessibleGarden) {
    baseScore += securityWeights.locationModifiers.hasAccessibleGarden;
  }
  if (hasAccessibleBalcony) {
    baseScore += securityWeights.locationModifiers.hasAccessibleBalcony;
  }

  // 4. Modificateurs environnementaux
  if (streetLighting !== '') {
    const modifier = securityWeights.locationModifiers.streetLighting[streetLighting];
    if (modifier !== undefined) {
      baseScore += modifier;
    }
  }

  if (streetVisibility !== '') {
    const modifier = securityWeights.locationModifiers.streetVisibility[streetVisibility];
    if (modifier !== undefined) {
      baseScore += modifier;
    }
  }

  if (neighborhoodType !== '') {
    const modifier = securityWeights.locationModifiers.neighborhoodType[neighborhoodType];
    if (modifier !== undefined) {
      baseScore += modifier;
    }
  }

  // 5. NOUVEAUX MODIFICATEURS - Profil d'occupation
  if (formData.occupationType && formData.occupationType !== '') {
    const modifier = securityWeights.occupationModifiers[formData.occupationType];
    if (modifier !== undefined) {
      baseScore += modifier;
    }
  }
  
  if (formData.hasPet) {
    baseScore += securityWeights.petBonus;
  }
  
  if (formData.presenceRhythm && formData.presenceRhythm !== '') {
    const modifier = securityWeights.presenceModifiers[formData.presenceRhythm];
    if (modifier !== undefined) {
      baseScore += modifier;
    }
  }

  // 6. NOUVEAUX MODIFICATEURS - Architecture avancée
  if (formData.doorMaterial && formData.doorMaterial !== '') {
    const modifier = securityWeights.doorMaterialModifiers[formData.doorMaterial];
    if (modifier !== undefined) {
      baseScore += modifier;
    }
  }
  
  if (formData.windowType && formData.windowType !== '') {
    const modifier = securityWeights.windowTypeModifiers[formData.windowType];
    if (modifier !== undefined) {
      baseScore += modifier;
    }
  }
  
  if (formData.hasShutters && formData.hasShutters !== '') {
    const modifier = securityWeights.shuttersModifiers[formData.hasShutters];
    if (modifier !== undefined) {
      baseScore += modifier;
    }
  }
  
  if (formData.sideAccess && formData.sideAccess !== '') {
    const modifier = securityWeights.sideAccessModifiers[formData.sideAccess];
    if (modifier !== undefined) {
      baseScore += modifier;
    }
  }

  // 7. Calcul scores par catégorie
  const rawCategoryScores: Record<string, number> = {
    protection: 0,
    surveillance: 0,
    deterrence: 0
  };

  equipment.forEach(equipmentId => {
    const equipment = securityWeights.equipment[equipmentId as keyof typeof securityWeights.equipment];
    if (equipment) {
      equipment.categories.forEach(category => {
        rawCategoryScores[category] += equipment.score;
      });
    }
  });

  // 8. Application plafonds
  const cappedCategoryScores: Record<string, number> = {
    protection: applyCategoryDiminishing(rawCategoryScores.protection, 'protection'),
    surveillance: applyCategoryDiminishing(rawCategoryScores.surveillance, 'surveillance'),
    deterrence: applyCategoryDiminishing(rawCategoryScores.deterrence, 'deterrence')
  };

  // 9. Normalisation
  const normalizedCategoryScores: Record<string, number> = {
    protection: Math.min(95, Math.round((cappedCategoryScores.protection / securityWeights.categoryLimits.protection.maxScore) * 100)),
    surveillance: Math.min(95, Math.round((cappedCategoryScores.surveillance / securityWeights.categoryLimits.surveillance.maxScore) * 100)),
    deterrence: Math.min(95, Math.round((cappedCategoryScores.deterrence / securityWeights.categoryLimits.deterrence.maxScore) * 100))
  };

  // 10. Score final
  // Le score final est la moyenne pondérée des catégories, ajustée par le baseScore (modificateurs contextuels)
  let categoryWeightedScore = calculateWeightedScore(normalizedCategoryScores, formData);

  // Le baseScore représente les modificateurs contextuels (±20 points max environ)
  // On l'applique comme un bonus/malus sur le score des catégories
  let finalScore = categoryWeightedScore + (baseScore * 0.15); // 15% d'influence du contexte

  // Plafonnement strict à globalScoreCap (95)
  finalScore = Math.max(0, Math.min(securityWeights.globalScoreCap, Math.round(finalScore)));

  return {
    score: finalScore,
    details: [
      {
        label: "Protection physique",
        score: normalizedCategoryScores.protection,
        maxScore: 100,
        percentage: normalizedCategoryScores.protection
      },
      {
        label: "Surveillance",
        score: normalizedCategoryScores.surveillance,
        maxScore: 100,
        percentage: normalizedCategoryScores.surveillance
      },
      {
        label: "Dissuasion",
        score: normalizedCategoryScores.deterrence,
        maxScore: 100,
        percentage: normalizedCategoryScores.deterrence
      }
    ],
    categoryScores: normalizedCategoryScores
  };
}

export function getRecommendations(formData: DiagnosticFormData): Recommendation[] {
  const { categoryScores } = calculateSecurityScore(formData);
  const recommendations: Recommendation[] = [];

  // Normalisation défensive
  const equipment: string[] = Array.isArray(formData.equipment) ? formData.equipment : [];
  const housingType = formData.housingType ?? 'house';
  const hasAccessibleGarden = formData.hasAccessibleGarden ?? false;

  if (categoryScores.protection < 65) {
    recommendations.push({
      title: "Protection physique à renforcer",
      priority: categoryScores.protection < 35 ? "high" : "medium",
      items: [
        !equipment.includes('door') ? "Installer une porte blindée" : "",
        !equipment.includes('smartLock') ? "Équiper les portes de serrures connectées" : "",
        !equipment.includes('windowLocks') ? "Installer des serrures de sécurité sur les fenêtres" : "",
        !equipment.includes('bars') && housingType === 'house' ?
          "Équiper les fenêtres accessibles de barreaux ou de vitrage sécurisé" : "",
        !equipment.includes('safe') ? "Installer un coffre-fort pour les objets de valeur" : "",
        hasAccessibleGarden && !equipment.includes('securityGate') ?
          "Sécuriser le périmètre avec un portail ou une clôture haute" : ""
      ].filter(Boolean)
    });
  }

  if (categoryScores.surveillance < 65) {
    recommendations.push({
      title: "Système de surveillance à améliorer",
      priority: categoryScores.surveillance < 35 ? "high" : "medium",
      items: [
        !equipment.includes('alarm') ? "Installer un système d'alarme" : "",
        !equipment.includes('camera') ? "Mettre en place des caméras de surveillance" : "",
        !equipment.includes('motionSensor') ? "Installer des détecteurs de mouvement" : "",
        !equipment.includes('doorSensor') ? "Équiper portes, fenêtres et volets de détecteurs" : "",
        !equipment.includes('videoDoorbell') ? "Installer une sonnette vidéo connectée" : "",
        !equipment.includes('intercom') ? "Installer un interphone ou visiophone" : ""
      ].filter(Boolean)
    });
  }

  if (categoryScores.deterrence < 65) {
    recommendations.push({
      title: "Mesures de dissuasion à mettre en place",
      priority: categoryScores.deterrence < 35 ? "high" : "medium",
      items: [
        !equipment.includes('lighting') ? "Installer un éclairage extérieur avec détecteur de mouvement" : "",
        !equipment.includes('fogSystem') && categoryScores.protection < 50 ?
          "Envisager l'installation d'un système de brouillard anti-intrusion" : "",
        "Mettre en place une signalétique de sécurité visible",
        "Maintenir une apparence d'occupation pendant les absences"
      ].filter(Boolean)
    });
  }

  return recommendations;
}

export function getVulnerableAreas(formData: DiagnosticFormData): VulnerableArea[] {
  const vulnerableAreas: VulnerableArea[] = [];

  // Normalisation défensive
  const equipment: string[] = Array.isArray(formData.equipment) ? formData.equipment : [];
  const housingType = formData.housingType ?? 'house';

  if (!equipment.includes('door')) {
    vulnerableAreas.push({
      label: "Points d'accès principaux",
      risk: 'high',
      description: "Les portes ne sont pas suffisamment sécurisées"
    });
  }

  if (!equipment.includes('camera') && !equipment.includes('alarm')) {
    vulnerableAreas.push({
      label: "Surveillance",
      risk: 'high',
      description: "Absence de système de surveillance"
    });
  }

  if (!equipment.includes('bars') && housingType === 'house') {
    vulnerableAreas.push({
      label: "Fenêtres et ouvertures",
      risk: 'medium',
      description: "Protection des fenêtres à renforcer"
    });
  }

  return vulnerableAreas;
}

export function getRiskLevel(score: number): 'high' | 'medium' | 'low' {
  if (score < securityWeights.vulnerabilityThresholds.high) return 'high';
  if (score < securityWeights.vulnerabilityThresholds.medium) return 'medium';
  return 'low';
}